import React, { useMemo } from 'react'
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles'

export function isObject(item) {
  return item && typeof item === 'object' && !Array.isArray(item)
}

export function mergeDeep(target, ...sources) {
  if (!sources.length) return target
  const source = sources.shift()

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} })
        mergeDeep(target[key], source[key])
      } else {
        Object.assign(target, { [key]: source[key] })
      }
    }
  }

  return mergeDeep(target, ...sources)
}

const defaultColors = {
  primary: {
    main: '#4285F4'
  },
  secondary: {
    main: '#262f3d'
  }
}

const defaultFonts = [
  { key: 'arial', label: 'Arial, sans-serif' },
  { key: 'alegreya', label: 'Alegreya, serif' },
  { key: 'stix_two_text', label: 'STIX Two Text, serif' },
  { key: 'pt_sans', label: 'PT Sans, sans-serif' },
  { key: 'roboto', label: 'Roboto, sans-serif' },
  { key: 'roboto_condensed', label: 'Roboto Condensed, sans-serif' },
  { key: 'external', label: 'Font External' }
]

const useFonts = (appData, fonts, hasSize) => {
  const fontTheme =
    fonts
      .filter((font) => font.key === appData?.theme?.fontFamily)
      .map((font, index) => (font[index] = `${hasSize ? font.size : font.label}`))
      .join('') || undefined

  const isFontExternal = fontTheme === 'Font External'
  let external = null

  if (isFontExternal) {
    external = {
      fontFamily: 'Font External',
      fontStyle: 'normal',
      fontDisplay: 'swap',
      fontWeight: 400,
      src: `url(${appData?.theme?.fontExternal}) format('woff2')`
    }
  }

  return {
    font: fontTheme ?? 'Roboto, Regular',
    isFontExternal,
    external
  }
}

const defaultButton = {
  height: 48,
  padding: '0 16px',
  fontSize: 16,
  lineHeight: '24px',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  minWidth: 'auto',
  '&:hover': {
    boxShadow: 'none'
  }
}

const devassaButton = {
  fontWeight: 'bold'
}

const createMyTheme = (appData = {}) => {
  const { font, isFontExternal, external } = useFonts(appData, defaultFonts, null)

  const customTheme = createTheme({
    typography: {
      useNextVariants: true,
      fontFamily: font,
      fontSize: 14,
      h1: {
        fontSize: 24
      },
      productTitle: {
        fontSize: 24,
        fontWeight: 'bold'
      }
    },
    palette: {
      type: 'light',
      ...(appData.colorPrimary && appData.colorSecondary && !appData.theme
        ? {
            primary: {
              main: appData.colorPrimary
            },
            secondary: {
              main: appData.colorPrimary
            }
          }
        : appData.theme
        ? {
            primary: {
              main: appData?.theme?.colorAction
            },
            secondary: {
              main: appData?.theme?.colorSearch
            }
          }
        : appData.colors
        ? appData.colors
        : defaultColors),
      background: {
        paper: '#fff',
        default: '#fff'
      }
    },
    shape: {
      borderRadius: 0
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 930,
        lg: 1280,
        xl: 1920
      }
    },
    overrides: {
      MuiTooltip: {
        tooltip: {
          fontSize: '0.9rem'
        }
      },
      MuiCssBaseline: {
        '@global': {
          '@font-face': isFontExternal ? [external] : ''
        }
      },
      MuiButton: {
        // gofind button standard
        contained: defaultButton,
        text: defaultButton,
        outlined: defaultButton
      },
      MuiTextField: {
        root: {
          '& legend': {
            float: 'none'
          }
        }
      }
    },
    gofind: {
      desktopMaxWidth: 930,
      asideWidth: 408,
      scrollBar: {
        '&::-webkit-scrollbar': { width: 6, borderRadius: 6 },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#EBEBEB',
          borderRadius: 6
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0, 0, 0, .40)',
          borderRadius: 6
        }
      },
      lineLimit: (n = 1) => ({
        display: '-webkit-box',
        '-webkit-line-clamp': n,
        '-webkit-box-orient': 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      })
    }
  })

  return customTheme
}

const customTheme = (config) => ({
  devassa: {
    palette: {
      primary: {
        main: '#D32730',
        light: '#ff605a',
        dark: '#9a0009',
        contrastText: '#fff'
      },
      text: {
        primary: '#000'
      },
      action: {
        disabledBackground: '#D3273026',
        disabled: '#fff'
      }
    },
    typography: {
      h1: {
        textTransform: 'uppercase',
        fontSize: 24,
        fontWeight: 'bold'
      },
      subtitle1: {
        fontWeight: 'bold',
        textTransform: 'uppercase'
      },
      productTitle: {
        textTransform: 'uppercase'
      }
    },
    shape: {
      borderRadius: 4
    },
    overrides: {
      MuiButton: {
        contained: devassaButton,
        text: devassaButton,
        outlined: devassaButton
      }
    }
  },
  tiger: {
    palette: {
      type: 'dark',
      primary: {
        main: '#ED8B00',
        light: '#ffbb44',
        dark: '#b45d00',
        contrastText: '#1F2D47'
      },
      text: { primary: '#fff', secondary: 'rgba(255,255,255, .54)' },
      background: {
        paper: '#1F2D47',
        default: '#1F2D47'
      },
      action: {
        disabledBackground: '#ED8B0026',
        disabled: '#1F2D47'
      }
    }
  },
  amstel: {
    palette: {
      type: 'light',
      primary: {
        main: '#ED1C24',
        light: '#FF5F4E',
        dark: '#B20000',
        contrastText: '#FFF'
      },
      text: { primary: '#131211', secondary: '#13121154' },
      background: {
        paper: '#FFF',
        default: '#FFF'
      },
      action: {
        disabledBackground: '#ED1C2426',
        disabled: '#fff'
      }
    },
    overrides: {
      MuiButton: {
        contained: { textTransform: 'none' },
        text: { textTransform: 'none' },
        outlined: { textTransform: 'none' }
      }
    }
  },
  sol: {
    palette: {
      type: 'light',
      primary: {
        main: '#FAB520',
        light: '#FFE759',
        dark: '#C28600',
        contrastText: '#1A1A18'
      },
      text: { primary: '#1A1A18', secondary: '#1A1A1854' },
      background: {
        paper: '#FFF',
        default: '#FFF'
      },
      action: {
        disabledBackground: '#FAB52026',
        disabled: '#1A1A18'
      }
    },
    overrides: {
      MuiButton: {
        contained: { textTransform: 'none' },
        text: { textTransform: 'none' },
        outlined: { textTransform: 'none' }
      }
    }
  },
  fys: {
    palette: {
      type: 'light',
      primary: {
        main: '#FFE100',
        light: '#FFFF54',
        dark: '#C7B000',
        contrastText: '#160D0D'
      },
      text: { primary: '#262626', secondary: '#26262654' },
      background: {
        paper: '#FFF',
        default: '#FFF'
      },
      action: {
        disabledBackground: '#FFE10026',
        disabled: '#160D0D'
      }
    },
    overrides: {
      MuiButton: {
        contained: { textTransform: 'none' },
        text: { textTransform: 'none' },
        outlined: { textTransform: 'none' }
      }
    }
  },
  eisenbahn: {
    palette: {
      primary: {
        main: '#221F20',
        light: '#494647',
        dark: '#000000',
        contrastText: '#fff'
      },
      text: { primary: '#212020', secondary: '#21202054' },
      action: {
        disabledBackground: '#221F2026',
        disabled: '#fff'
      }
    }
  },
  badenbaden: {
    palette: {
      type: 'dark',
      primary: {
        main: '#E6E6E6',
        light: '#FFF',
        dark: '#B4B4B4',
        contrastText: '#232323'
      },
      text: { primary: '#F5F5F4', secondary: '#F5F5F454' },
      background: {
        paper: '#232323',
        default: '#232323'
      },
      action: {
        disabledBackground: '#E6E6E626',
        disabled: '#232323'
      }
    },
    shape: { borderRadius: 4 },
    overrides: {
      MuiButton: {
        contained: { textTransform: 'none' },
        text: { textTransform: 'none' },
        outlined: { textTransform: 'none' }
      }
    }
  },
  heineken: {
    typography: {
      productTitle: {
        fontWeight: 'light'
      }
    },
    palette: {
      primary: {
        main: '#205527',
        light: '#4d8250',
        dark: '#002b00',
        contrastText: '#fff'
      },
      action: {
        disabledBackground: '#20552726',
        disabled: '#fff'
      }
    },
    overrides: {
      MuiButton: {
        contained: { textTransform: 'none' },
        text: { textTransform: 'none' },
        outlined: { textTransform: 'none' }
      }
    }
  },
  default: {} // sempre vazio
})

export default function Theme({ children, config }) {
  const theme = useMemo(() => {
    let result = createMyTheme(config)

    const override = customTheme(config)[config?.brand?.toLowerCase()?.trim() || 'default']

    const themeResult = mergeDeep(result, override)

    window.theme = themeResult

    return themeResult
  }, [config])

  return <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
}
